import { Input } from 'components/Forms'
import { InputUsPhone } from 'components/Forms/InputUsPhone'
import { ReusableModal } from 'components/Modals/ReusableModal'
import { LangEnum } from 'configs/lang'
import { useEffect, useState } from 'react'
import { Collapse } from 'react-bootstrap'
import { FaAngleDown, FaAngleUp, FaExclamationTriangle } from 'react-icons/fa'
import { Button } from '@outbound-ai/ursa'
import { css } from 'styled-components'
import { v4 } from 'uuid'
import { IUserPhone, SettingsConstEnum } from '../settingsTypes'

interface IProps {
  handleAddPhone: (newPhone: IUserPhone) => void
  title: JSX.Element
  isButtonDisabled?: boolean
  phoneDetails?: IUserPhone
  isCallInProgress?: boolean
}
export const SettingsAddEditPhoneModal = ({
  handleAddPhone,
  title,
  isButtonDisabled = false,
  phoneDetails: phoneData = {
    value: '',
    name: '',
    id: v4(),
  },
  isCallInProgress = false,
}: IProps) => {
  const [show, setShow] = useState(false)
  const [phoneDetails, setPhoneDetails] = useState<IUserPhone>(phoneData)
  const [openNotice, setOpenNotice] = useState(false)

  useEffect(() => {
    setPhoneDetails(phoneData)
  }, [])

  const modalStyle = {
    showCloseButton: true,
    headerstyles: css`
      background-color: ${({ theme }) => theme.colors.grey.lightest};
      border-bottom: none;
      padding: ${({ theme }) => theme.util.pixelToRem(30)} ${({ theme }) => theme.util.pixelToRem(24)} 0.25rem
        ${({ theme }) => theme.util.pixelToRem(62)};
      span {
        font-weight: ${({ theme }) => theme.fontWeight.bold};
        color: ${({ theme }) => theme.colors.primary.darker};
        font-size: ${({ theme }) => theme.typography.h4};
      }
      .btn-close {
        opacity: 1;
        background: ${({ theme }) => `transparent
      url('data:image/svg+xml,%3csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 16 16%27 fill=%27%23${theme.colors.primary.neutral.replace(
        '#',
        '',
      )}%27%3e%3cpath d=%27M.293.293a1 1 0 011.414 0L8 6.586 14.293.293a1 1 0 111.414 1.414L9.414 8l6.293 6.293a1 1 0 01-1.414 1.414L8 9.414l-6.293 6.293a1 1 0 01-1.414-1.414L6.586 8 .293 1.707a1 1 0 010-1.414z%27/%3e%3c/svg%3e')
      center/1em auto no-repeat`};
      }
    `,
    bodystyles: css`
      padding: 0px ${({ theme }) => theme.util.pixelToRem(62)} ${({ theme }) => theme.util.pixelToRem(36)};
      p {
        font-size: ${({ theme }) => theme.util.pixelToRem(13)};
        color: ${({ theme }) => theme.colors.grey.darker};
      }

      .notice-container {
        margin-top: 1rem;
        margin-bottom: 1.5rem;
        padding: 0.25rem 0.5rem;
        background-color: ${({ theme }) => theme.colors.warning.lightest};
        border: 1px solid ${({ theme }) => theme.colors.warning.light};
        border-radius: ${({ theme }) => theme.borderRadius.normal};
        .notice-toggler {
          display: flex;
          justify-content: space-between;
          align-items: center;
          cursor: pointer;
          span:first-child {
            color: ${({ theme }) => theme.colors.warning.darker};
            font-size: ${({ theme }) => theme.typography.xxs};
            font-weight: ${({ theme }) => theme.fontWeight.bold};
            svg {
              position: relative;
              bottom: 1px;
              color: ${({ theme }) => theme.colors.warning.dark};
            }
          }
          span:last-child {
            color: ${({ theme }) => theme.colors.warning.darker};
          }
        }
        #notice-collapse {
          p {
            color: ${({ theme }) => theme.colors.warning.darker};
            font-size: ${({ theme }) => theme.typography.xxs};
          }
        }
      }

      .input-container {
        margin-bottom: ${({ theme }) => theme.util.pixelToRem(36)};
        .labelText {
          color: ${({ theme }) => theme.colors.grey.darker};
        }
        input {
          outline: 1px solid ${({ theme }) => theme.colors.primary.lighter};
        }
        .phone-number-container {
          input {
            width: ${({ theme }) => theme.util.pixelToRem(226)};
          }
        }
      }
    `,
  }
  const handleSetPhoneNumber = (value: string) => {
    setPhoneDetails((details) => ({ ...details, value }))
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target as HTMLInputElement
    setPhoneDetails((details) => ({ ...details, name: value }))
  }

  const onConfirm = () => {
    handleAddPhone(phoneDetails)
    setShow(false)
  }

  const isValidUsNumberPattern = (value: string) => {
    const floatValue = value.replace(' ', '').replace('(', '').replace(')', '').replace('_', '')
    return floatValue.length === SettingsConstEnum.usPhoneNumberLength
  }

  const hasValidData = isValidUsNumberPattern(phoneDetails.value) && phoneDetails.name.trim().length
  return (
    <div>
      <ReusableModal
        show={show}
        hidden={true}
        setShow={setShow}
        toggler={
          <Button variant="info" size="small" onClick={() => setShow(true)} disabled={isButtonDisabled}>
            {title}
          </Button>
        }
        maxWidth="33rem"
        style={modalStyle}
        modalHeader={<span>Third Party Phone</span>}
      >
        <p>
          {LangEnum.claims} {LangEnum.work} will call the phone number you provide below, so that you can speak and
          listen to call audio from your own device when you take over payer phone calls.
        </p>
        <section
          className="notice-container"
          onClick={() => setOpenNotice((oldOpen) => !oldOpen)}
          aria-controls="notice-collapse"
          aria-expanded={openNotice}
        >
          <section className="notice-toggler">
            <span>
              <FaExclamationTriangle size={10} /> Confidentiality Notice
            </span>
            <span>{openNotice ? <FaAngleUp size={12} /> : <FaAngleDown size={12} />}</span>
          </section>
          <Collapse in={openNotice}>
            <section id="notice-collapse">
              <p>
                Please only add phone numbers to third party phones that you trust and will personally use to conduct
                business. Calls containing PHI will be heard via that third party phone.
              </p>
            </section>
          </Collapse>
        </section>
        <section className="input-container">
          <Input
            type="text"
            label="Device Name"
            name="name"
            id="name"
            onChange={handleChange}
            value={phoneDetails.name}
          />
          <section className="mb-4 phone-number-container">
            <InputUsPhone
              label="Phone Number"
              name="phoneNumber"
              onChange={handleSetPhoneNumber}
              value={phoneDetails.value}
              required
            />
          </section>
        </section>

        <div className="d-flex align-items-center justify-content-center gap-2">
          <Button size="large" variant="primary-outline" onClick={() => setShow(false)}>
            Cancel
          </Button>
          <Button size="large" variant="primary" onClick={onConfirm} disabled={!hasValidData || isCallInProgress}>
            Save Phone Details
          </Button>
        </div>
      </ReusableModal>
    </div>
  )
}
